import React, { Fragment, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import isNil from 'lodash-es/isNil';
import { Locale } from '@yi/core/src';

import { Link } from 'gatsby';
import { SimpleResponsiveImage } from '~/src/common/image/SimpleResponsiveImage';
import { useIsGreaterThanTablet } from '~/src/common/mediaQueries/useIsGreaterThanTablet';
import { CountdownDetails } from '~/src/common/promo/countdown-timer';
import { useEndOfTheDayCountdownTimer } from '~/src/common/promo/countdown-timer/useEndOfTheDayCountdownTimer';
import { useLastNHoursCountdownTimer } from '~/src/common/promo/countdown-timer/useLastNHoursCountdownTimer';
import { formatToTwoDigits } from '~/src/common/utilities/number-utils';
import { navigate } from '~/src/common/utilities/utilities';
import { Button } from '~/src/components/buttons';
import { useIsSSR } from '~/src/components/hooks/useIsSSR';
import { useLocale } from '~/src/components/LocaleProvider';
import { breakpoints, typography } from '~/src/styles';
import { leftPanelZIndex } from '~/src/styles/styled-variables';

import { ReactComponent as ChevronRight } from './forward-arrow.svg';
import { i18nPhrasesTimer } from './i18nPhrasesTimer';
import { useAnnualSaleDetails } from './useAnnualSaleDetails';
import { SiteWideBanner } from '../SiteWideBanner';

const gridAreas = {
  img: 'img',
  btn: 'btn',
  content: 'content',
};

const contentGridAreas = {
  subtitle: 'subtitle',
  title: 'title',
  img: 'img',
  countdown: 'countdown',
};

const XS_IMG_SRC =
  'https://brooklyn.gaia.com/v1/assets-render/085f3645-47e6-4f38-96b5-fa2f09cf4174?hash=c5309a93e9a10f0e6fac0e3003892d672eca39d63d823a5121f2d39cbdb665f4';

const XL_IMG_SRC =
  'https://brooklyn.gaia.com/v1/assets-render/e98939b9-d655-42c1-983e-3b6d2d8dbcba?hash=c5309a93e9a10f0e6fac0e3003892d672eca39d63d823a5121f2d39cbdb665f4';

const PHASE2_XS_IMG_SRC =
  'https://brooklyn.gaia.com/v1/assets-render/d465c4c4-1561-47fe-9cf4-c1ae38a61a0c?hash=c5309a93e9a10f0e6fac0e3003892d672eca39d63d823a5121f2d39cbdb665f4';

const PHASE2_XL_IMG_SRC =
  'https://brooklyn.gaia.com/v1/assets-render/bc575a60-a3d3-41f6-8ab1-9664a3ea179a?hash=c5309a93e9a10f0e6fac0e3003892d672eca39d63d823a5121f2d39cbdb665f4';

const styles = {
  container: css({
    width: '100%',
    background: '#28292C',
    display: 'flex',
    justifyContent: 'center',
  }),
  grid: css({
    maxWidth: '1500px',
    width: '100%',
    height: '63px',
    overflow: 'hidden',
    justifyContent: 'space-between',
    alignItems: 'center',
    justifyItems: 'left',
    display: 'grid',
    gridTemplateAreas: `
      '. ${gridAreas.img} . ${gridAreas.content} . ${gridAreas.btn} .'
    `,
    gridTemplateColumns: `minmax(2px, 1fr) 55px 1fr auto 1fr auto minmax(4px, 1fr)`,
    [breakpoints.minMobileLandscape]: {
      gridTemplateColumns: `minmax(2px, 1fr) 46px 1fr auto 1fr auto minmax(4px, 1fr)`,
      height: '56px',
    },
    [breakpoints.minTablet]: {
      gridTemplateColumns: `4px 75px 1fr auto 1fr auto 4px`,
      height: '63px',
    },
    [breakpoints.minTabletLandscape]: {
      gridTemplateColumns: `4px 150px 1fr auto 1fr auto  4px`,
    },
    [breakpoints.minDesktop]: {
      gridTemplateColumns: `minmax(8px,1fr) 200px 0.5fr auto  1fr auto minmax(8px,1fr)`,
      height: '80px',
    },
  }),
  btnContainer: css({
    marginTop: 'var(--Item-Vertical-Margin)',
    gridArea: gridAreas.btn,
    display: 'none !important',
    [breakpoints.minMobileLandscape]: {
      display: 'block !important',
    },
  }),
  iconBtnContainer: css({
    marginTop: 'var(--Item-Vertical-Margin)',
    gridArea: gridAreas.btn,
    display: 'block !important',
    [breakpoints.minMobileLandscape]: {
      display: 'none !important',
    },
  }),
  btn: css({
    textWrap: 'nowrap',
    fontSize: '12px',
    paddingLeft: '10px',
    paddingRight: '10px',
    fontWeight: '700',
    letterSpacing: '0.6px',
    height: '35px',
    [breakpoints.minTablet]: {
      height: '40px',
      fontSize: '15px',
      fontWeight: '700',
      lineHeight: '20px',
      letterSpacing: '0.45px',
      paddingLeft: '16px',
      paddingRight: '16px',
    },
    [breakpoints.minDesktop]: {
      height: '52px',
      fontSize: '16px',
      fontWeight: '700',
      lineHeight: '20px',
      letterSpacing: '0.8px',
      paddingLeft: '16px',
      paddingRight: '16px',
    },
  }),
  iconBtn: css({
    display: 'block',
    height: '25px',
    [breakpoints.minMobileLandscape]: {
      height: '18px',
    },
  }),
  img: css({
    width: '100%',
    gridArea: gridAreas.img,
    overflow: 'hidden',
  }),
  imgXS: css({
    display: 'block',
    [breakpoints.minTabletLandscape]: {
      display: 'none',
    },
  }),
  imgXL: css({
    display: 'none',
    [breakpoints.minTabletLandscape]: {
      display: 'block',
    },
  }),
  timeLeftContainer: css({
    alignSelf: 'end',
    gridArea: contentGridAreas.countdown,
    textAlign: 'center',
    gridTemplateColumns: '1fr auto 1fr auto 1fr auto 1fr',
    display: 'inline-grid',
    color: 'var(--White, #FFF)',
    fontFamily: 'Montserrat',
    fontSize: '13px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '20px',
    letterSpacing: '0.36px',
    [breakpoints.minTablet]: [
      typography.tabletLabelsBold,
      {
        alignSelf: 'unset',
        fontWeight: '600',
        columnGap: '1px',
      },
    ],
  }),
  timeLeftLegend: css({
    fontSize: '8px',
    fontWeight: '600',
    lineHeight: '16px',
    letterSpacing: '0.4px',
    gridRow: 2,
    justifySelf: 'center',
    display: 'none',
    [breakpoints.minTablet]: {
      display: 'block',
    },
  }),
  timeLeft: css({
    justifySelf: 'center',
    gridRow: 1,
    display: 'flex',
    lineHeight: '20px',

    '& > .digit': {
      width: '9px',
    },
    [breakpoints.minMobileLandscape]: {
      '& > .digit:first-child': {
        textAlign: 'right',
      },
      '& > .digit:last-child': {
        textAlign: 'left',
      },
    },
    [breakpoints.minTablet]: {
      lineHeight: '1em',
      '& > .digit': {
        width: '11.5px',
      },
    },
  }),
  timeLeftSeparator: css({
    lineHeight: '20px',
    gridRow: 1,
    [breakpoints.minTablet]: {
      lineHeight: '1em',
    },
  }),
};

const timeDetailKeys: Array<keyof CountdownDetails> = [
  'days',
  'hours',
  'minutes',
  'seconds',
] as const;

const i18nPhrases = {
  a_year_of_yoga: (locale: Locale) => {
    const en = `A YEAR OF YOGA`;
    const phrases: { [key: string]: undefined | string } = {
      en,
      es: `EN UN AÑO DE YOGA`,
    };
    return phrases[locale] || en;
  },
  a_year_of_yoga_for: (locale: Locale) => {
    const en = `A Year of Yoga for`;
    const phrases: { [key: string]: undefined | string } = {
      en,
      // TODO Add translations
      es: ``,
    };
    return phrases[locale] || en;
  },
  a_year_of_unlimited_yoga_for_only: (locale: Locale) => {
    const en = `A Year of Unlimited Yoga for Only`;
    const phrases: { [key: string]: undefined | string } = {
      en,
      // TODO Add translations
      es: ``,
    };
    return phrases[locale] || en;
  },
  free_bonus: (locale: Locale) => {
    const en = `Free Bonus!`;
    const phrases: { [key: string]: undefined | string } = {
      en,
      // TODO Add translations
      es: ``,
    };
    return phrases[locale] || en;
  },
  free_pose_guide: (locale: Locale) => {
    const en = `Free Pose Guide!`;
    const phrases: { [key: string]: undefined | string } = {
      en,
      // TODO Add translations
      es: ``,
    };
    return phrases[locale] || en;
  },
  only: (locale: Locale) => {
    const en = `Only`;
    const phrases: { [key: string]: undefined | string } = {
      en,
      // TODO Add translations
      es: ``,
    };
    return phrases[locale] || en;
  },
  unlock_offer: (locale: Locale) => {
    const en = `Unlock Offer!`;
    const phrases: { [key: string]: undefined | string } = {
      en,
      // TODO Add translations
      es: ``,
    };
    return phrases[locale] || en;
  },
  get_offer: (locale: Locale) => {
    const en = `Get Offer!`;
    const phrases: { [key: string]: undefined | string } = {
      en,
      // TODO Add translations
      es: ``,
    };
    return phrases[locale] || en;
  },
};

function useClickableOnMobileBanner({ url }: { url: string }) {
  const isGreaterThanTablet = useIsGreaterThanTablet();
  const isWholeBannerClickable = !isGreaterThanTablet; // mobile only
  const goToSalesPage = useCallback(() => {
    navigate(url, {}, { doNotLocalize: true });
  }, [url]);

  const params = useMemo(() => {
    if (isWholeBannerClickable) {
      return { role: 'link', onClick: goToSalesPage };
    }
    return {};
  }, [goToSalesPage, isWholeBannerClickable]);

  return params;
}

const titleCss = css({
  gap: '0.5em',
  textWrap: 'nowrap',
  gridArea: contentGridAreas.title,
  color: 'var(--White, #FFF)',
  fontFamily: 'Montserrat',
  fontSize: '13px',
  fontStyle: 'normal',
  fontWeight: '600',
  lineHeight: '20px',
  letterSpacing: '0.65px',
  '& > .mobileLandscape,& > .tablet': {
    display: 'none',
  },
  '& > .mobile': {
    display: 'inline',
  },
  [breakpoints.minMobileLandscape]: {
    fontSize: '12px',
    letterSpacing: '0.36px',
    '& > .mobile,& > .tablet': {
      display: 'none',
    },
    '& > .mobileLandscape': {
      display: 'inline',
    },
  },
  [breakpoints.minTablet]: {
    '& > .mobile,& > .mobileLandscape': {
      display: 'none',
    },
    '& > .tablet': {
      display: 'inline',
    },
  },
  [breakpoints.minTabletLandscape]: {
    fontSize: '16px',
    lineHeight: '22px',
    letterSpacing: '0.8px',
  },
  [breakpoints.minDesktop]: {
    fontSize: '18px',
    lineHeight: '32px',
    letterSpacing: '0.9px',
  },
});

const subtitleCss = css({
  gridArea: contentGridAreas.subtitle,
  alignSelf: 'start',
  textWrap: 'nowrap',
  fontFamily: 'Montserrat',
  fontSize: '13px',
  fontStyle: 'normal',
  fontWeight: '600',
  lineHeight: '20px',
  letterSpacing: '0.65px',
  color: 'var(--White-Text)',
  '& > .text': {
    [breakpoints.minMobileLandscape]: {
      display: 'none',
    },
  },
  '& > .regularPrice': {
    color: 'var(--Primary)',
    fontWeight: '600',
  },
  '& > .discountPrice,& > .freeBonus': {
    color: '#46C62C',
    fontWeight: '700',
  },
  '& > .freeBonusLg': {
    display: 'none',
    [breakpoints.minTablet]: {
      display: 'inline',
    },
  },
  '& > .freeBonusSm': {
    display: 'inline',
    [breakpoints.minTablet]: {
      display: 'none',
    },
  },
  [breakpoints.minMobileLandscape]: {
    fontSize: '12px',
    letterSpacing: '0.36px',
    fontWeight: '600',
  },
  [breakpoints.minTablet]: [
    typography.tabletLabelsBold,
    {
      alignSelf: 'unset',
      fontWeight: '600',
      fontSize: '12px',
    },
  ],
  [breakpoints.minTabletLandscape]: [
    typography.tabletLabelsBold,

    {
      fontWeight: '600',
    },
  ],
  [breakpoints.minDesktop]: {
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '32px',
    letterSpacing: '0.9px',
  },
});

const contentGridCss = css({
  gridArea: gridAreas.content,
  width: '100%',
  overflow: 'hidden',
  justifyContent: 'space-between',
  alignItems: 'center',
  justifyItems: 'left',
  display: 'grid',
  columnGap: '0.5rem',
  gridTemplateAreas: `
      '${contentGridAreas.title} ${contentGridAreas.countdown} '
      '${contentGridAreas.subtitle} ${contentGridAreas.subtitle}'
    `,
  gridTemplateColumns: `auto 1fr`,
  [breakpoints.minMobileLandscape]: {
    columnGap: '0.2rem',
    gridTemplateAreas: `
      '${contentGridAreas.title} ${contentGridAreas.subtitle} ${contentGridAreas.countdown} '
    `,
    gridTemplateColumns: `auto auto auto`,
  },
  [breakpoints.minTablet]: {
    columnGap: '0.2rem',
    gridTemplateAreas: `
      '${contentGridAreas.title} ${contentGridAreas.subtitle} . ${contentGridAreas.countdown} '
    `,
    gridTemplateColumns: `auto auto 8px auto`,
  },
  [breakpoints.minDesktop]: {
    columnGap: '0.2rem',
    gridTemplateColumns: `auto auto 12px auto`,
  },
});

type BannerDetails = {
  url: string;
  regularPrice: string | null;
  discountPrice: string | null;
  isCurrencyISORequired: boolean;
  currencyIso: string;
  lastChance: boolean;
};

const Body: React.FC<BannerDetails & { isTimeLeft: boolean; timeDetails: CountdownDetails }> = ({
  discountPrice,
  isCurrencyISORequired,
  regularPrice,
  url,
  currencyIso,
  lastChance,
  isTimeLeft,
  timeDetails,
}) => {
  const { locale } = useLocale();

  const clickableOnMobileParams = useClickableOnMobileBanner({ url });

  if (isNil(regularPrice) || isNil(discountPrice) || !isTimeLeft) {
    return null;
  }
  return (
    <div css={styles.container} {...clickableOnMobileParams}>
      <div css={styles.grid}>
        <SimpleResponsiveImage
          css={[styles.img, styles.imgXS]}
          src={lastChance ? PHASE2_XS_IMG_SRC : XS_IMG_SRC}
          width={300}
        />
        <SimpleResponsiveImage
          css={[styles.img, styles.imgXL]}
          src={lastChance ? PHASE2_XL_IMG_SRC : XL_IMG_SRC}
          width={600}
        />
        <div css={contentGridCss}>
          <div css={titleCss}>
            <span className="mobile">{i18nPhrases.a_year_of_yoga(locale)} </span>
            <span className="mobileLandscape">{i18nPhrases.a_year_of_yoga_for(locale)} </span>
            <span className="tablet">{i18nPhrases.a_year_of_unlimited_yoga_for_only(locale)} </span>
          </div>
          <div css={styles.timeLeftContainer}>
            {timeDetailKeys.map((key, idx) => (
              <Fragment key={key}>
                <div css={styles.timeLeft}>
                  {[...formatToTwoDigits(timeDetails[key])].map((char, charIdx) => (
                    <div className="digit" key={charIdx}>
                      {char}
                    </div>
                  ))}
                </div>
                {idx !== timeDetailKeys.length - 1 && <div css={styles.timeLeftSeparator}>:</div>}
              </Fragment>
            ))}
            <div
              css={styles.timeLeftLegend}
              style={{
                gridColumn: 1,
              }}
            >
              {i18nPhrasesTimer.days(locale)}
            </div>
            <div css={styles.timeLeftLegend} style={{ gridColumn: 3 }}>
              {i18nPhrasesTimer.hrs(locale)}
            </div>
            <div css={styles.timeLeftLegend} style={{ gridColumn: 5 }}>
              {i18nPhrasesTimer.min(locale)}
            </div>
            <div css={styles.timeLeftLegend} style={{ gridColumn: 7 }}>
              {i18nPhrasesTimer.sec(locale)}
            </div>
          </div>
          <div css={subtitleCss}>
            <span className="text">{i18nPhrases.only(locale)} </span>
            <span
              className="regularPrice"
              style={{
                textDecoration: 'line-through',
              }}
            >
              {regularPrice}
            </span>
            <span className="discountPrice"> {discountPrice}</span>
            {isCurrencyISORequired && <span className="discountPrice"> {currencyIso}</span>}
            <span className="freeBonus freeBonusSm"> + {i18nPhrases.free_bonus(locale)}</span>
            <span className="freeBonus freeBonusLg"> + {i18nPhrases.free_pose_guide(locale)}</span>
          </div>
        </div>
        <Button
          to={url}
          kind="unstyled"
          as={Link}
          containerCss={styles.iconBtnContainer}
          css={styles.iconBtn}
        >
          <ChevronRight style={{ height: '100%' }} />
        </Button>
        <Button
          to={url}
          kind="primary"
          as={Link}
          containerCss={styles.btnContainer}
          css={styles.btn}
        >
          {i18nPhrases.get_offer(locale)}
        </Button>
      </div>
    </div>
  );
};

const LastChanceBody: React.FC<BannerDetails> = props => {
  const { timeDetails, isTimeLeft } = useLastNHoursCountdownTimer(6);

  return <Body {...props} timeDetails={timeDetails} isTimeLeft={isTimeLeft} />;
};
const InitialOfferBody: React.FC<BannerDetails> = props => {
  const { timeDetails, isTimeLeft } = useEndOfTheDayCountdownTimer();

  return <Body {...props} timeDetails={timeDetails} isTimeLeft={isTimeLeft} />;
};

const OfferSelect: React.FC<BannerDetails & { lastChance: boolean }> = props => {
  if (props.lastChance) {
    return <LastChanceBody {...props} />;
  }
  return <InitialOfferBody {...props} />;
};

const containerCss = css({
  position: 'sticky',
  top: 'var(--Header-Height)',
  zIndex: leftPanelZIndex - 1,
});

export const May2025Banner: React.FC<{ isShown: boolean; lastChance: boolean }> = ({
  isShown,
  lastChance,
}) => {
  const isSSR = useIsSSR();
  const { details, loading, hasMembershipAccess } = useAnnualSaleDetails();

  if (isSSR || loading || hasMembershipAccess) {
    return null;
  }

  return (
    <SiteWideBanner isShown={isShown} css={containerCss}>
      <OfferSelect {...details} lastChance={lastChance} />
    </SiteWideBanner>
  );
};
